// Condition reporting on the device: the job's visit, a visit area for each
// of the job's areas (one area list per job — DD 2026-10-21), a rating per
// surface, and its photos' rows linked to the visit as evidence. Everything is saved here first
// and synced like measuring (docs/OFFLINE.md); nothing needs signal.
import { bandOf, conditionRecordId } from '@higgins/condition-library';
import { addSurface, parseSurfaces, renameSurface, serialiseSurfaces, type SurfacesDoc, type SurfaceType } from '@higgins/m2q-pricing';
import { uploadedList } from './area-photos';
import { getJob, getRecord, listPhotos, listRecords, patchFields, saveRecord, type DeviceRecord, type Fields } from './device-db';
import { earlierPhotoFields, PHOTO_TABLE, photoRowsOf, photoRowsOfArea, removingIds } from './photo-rows';
import { BAND_CHOICE, DEFAULT_SCALE_LABEL, ratedOn, scaleKeyOf } from './condition';
import { isRemovedRating, nameChange, ratingId, REMOVED_STATE, type RatingLine } from './condition-lines';
import { restoreChanges } from './condition-restore';
import { syncNow } from './outbox';

/** The job's condition visit (one per job for now: the audit). */
export const visitOf = (records: readonly DeviceRecord[]): DeviceRecord | undefined => records.find((r) => r.table === 'visit');

export const visitAreaOf = (records: readonly DeviceRecord[], areaId: string): DeviceRecord | undefined =>
  records.find((r) => r.table === 'visitarea' && r.fields.hig_measureareaid === areaId);

/** A visit area's rating records, those removed from the report included (condition-lines.ts needs them). */
export const allObservationsOf = (records: readonly DeviceRecord[], visitAreaId: string): DeviceRecord[] =>
  records
    .filter((r) => r.table === 'observation' && r.parentId === visitAreaId)
    .sort((a, b) => Number(a.fields.hig_sortorder ?? 0) - Number(b.fields.hig_sortorder ?? 0));

/** A visit area's ratings on the report: a removed line's (#509) counts nowhere, the area's rating included. */
export const observationsOf = (records: readonly DeviceRecord[], visitAreaId: string): DeviceRecord[] =>
  allObservationsOf(records, visitAreaId).filter((o) => !isRemovedRating(o));

/** The table a condition photo has until scripts/migrate-photo-rows.ps1 moves it to a photo row (the API sends it while it hasn't). */
const UNMOVED_EVIDENCE = 'evidencephoto';

/** The job's photos that are evidence on its condition report: the photo rows linked to a visit area, and any not moved yet. */
export const evidenceOf = (records: readonly DeviceRecord[]): DeviceRecord[] => [
  ...photoRowsOf(records).filter((r) => typeof r.fields.hig_visitareaid === 'string'),
  ...records.filter((r) => r.table === UNMOVED_EVIDENCE),
];

/**
 * Start the job's condition report: the server makes the centre from the job
 * when it syncs. The visit's id comes from the job's, so two iPads starting the
 * same job's report make one visit, not two (ids.js in the library); the
 * child records' ids follow the same way.
 */
export async function startVisit(job: { id: string; number: string }, scaleLabel: string = DEFAULT_SCALE_LABEL, hasMeasure: boolean): Promise<string> {
  const id = conditionRecordId('visit', job.id);
  await saveRecord({
    id,
    table: 'visit',
    jobId: job.id,
    parentId: job.id,
    etag: null,
    fields: {
      hig_name: `${job.number} condition`,
      hig_visittype: 'Audit',
      hig_status: 'Draft',
      hig_includescondition: true,
      hig_includesmeasure: hasMeasure,
      hig_ratingscale: scaleLabel,
      hig_inspectedon: new Date().toISOString(),
    },
  });
  void syncNow();
  return id;
}

let tail: Promise<unknown> = Promise.resolve();

/** Run `work` after any still running: two at once could each make the same area's visit area (or a REVIEW answer's row). */
export function oneAtATime<T>(work: () => Promise<T>): Promise<T> {
  const next = tail.then(work, work);
  tail = next.catch(() => undefined);
  return next;
}

/**
 * The visit area for one of the job's areas, made the first time anything
 * condition is recorded on it. Its name and side follow the area's. Looked up
 * fresh from the device, one caller at a time, so it's only ever made once.
 */
export function ensureVisitArea(visit: DeviceRecord, area: DeviceRecord): Promise<DeviceRecord> {
  return oneAtATime(async () => {
    const existing = visitAreaOf(await listRecords(visit.jobId), area.id);
    return existing ?? makeVisitArea(visit, area);
  });
}

/** A visit area's id, from its visit and the job's area: known before the visit area is made. */
export const visitAreaIdFor = (visit: DeviceRecord, area: DeviceRecord): string => conditionRecordId('visitarea', visit.id, area.id);

async function makeVisitArea(visit: DeviceRecord, area: DeviceRecord): Promise<DeviceRecord> {
  const record = {
    id: visitAreaIdFor(visit, area),
    table: 'visitarea' as const,
    jobId: visit.jobId,
    parentId: visit.id,
    etag: null,
    fields: {
      hig_name: String(area.fields.hig_name ?? ''),
      hig_side: area.fields.hig_side ?? null,
      hig_measureareaid: area.id,
      hig_sortorder: Number(area.fields.hig_sortorder ?? 0),
    } satisfies Fields,
  };
  await saveRecord(record);
  return (await getRecord(record.id)) as DeviceRecord;
}

/** Change a visit area (ticks, action) — merged into the stored copy. */
export const updateVisitArea = (visitArea: DeviceRecord, changes: Fields): Promise<void> => patchFields(visitArea.id, changes);

/**
 * Rate a surface, or change its defects or note. A surface rated for the first
 * time gets its observation, its id from what it rates (condition-lines.ts). A
 * renamed surface's rating takes the new name with its next change. The band is
 * stored beside the value, worked out here.
 */
export async function saveObservation(visit: DeviceRecord, visitArea: DeviceRecord, line: RatingLine, sortOrder: number, changes: Fields): Promise<void> {
  const withBand = 'hig_rating' in changes ? { ...changes, hig_band: bandChoice(visit, changes.hig_rating), ...ratedOn(visit.fields.hig_ratingscale) } : changes;
  const { observation: existing } = line;
  if (existing) return patchFields(existing.id, { ...withBand, ...nameChange(existing, line) });
  await saveRecord({
    id: ratingId(visitArea.id, line.ratedBy),
    table: 'observation',
    jobId: visitArea.jobId,
    parentId: visitArea.id,
    etag: null,
    fields: { hig_name: line.name, hig_surfacetypeid: line.typeId, hig_basesurface: line.baseSurface, hig_sortorder: sortOrder, hig_noteedited: false, ...withBand },
  });
}

/**
 * Take a line off the condition report (#509): its rating record is made
 * inactive, never deleted, so the WHS history keeps it; a line never rated gets
 * an inactive record, so it stays off on every iPad. The surface on the Measure
 * tab and the area's photos are left as they are.
 */
export const removeRating = (visit: DeviceRecord, visitArea: DeviceRecord, line: RatingLine, sortOrder: number): Promise<void> =>
  saveObservation(visit, visitArea, line, sortOrder, { statecode: REMOVED_STATE });

/**
 * Bring a removed line back (#509): its rating record active again under the
 * line's name now, with its defects and note, and its rating only when it was
 * made on the report's scale now (condition-restore.ts); otherwise cleared.
 */
export async function restoreRating(visit: DeviceRecord, line: RatingLine): Promise<void> {
  const { observation } = line;
  if (!observation) return;
  await patchFields(observation.id, restoreChanges(observation, line, visit.fields.hig_ratingscale));
}

/** Change the area's measured surfaces as stored, never the screen's copy (CLAUDE.md): the Measure tab shows the result. */
function changeSurfaces(areaId: string, change: (doc: SurfacesDoc) => SurfacesDoc): Promise<void> {
  return patchFields(areaId, (stored) => ({
    hig_surfaces: serialiseSurfaces(change(parseSurfaces(typeof stored.hig_surfaces === 'string' ? stored.hig_surfaces : null))),
  }));
}

/**
 * "+ Add surface" on the Condition tab adds it to the area's measured surfaces
 * (#469), with nothing measured yet, so it waits on the Measure tab to be
 * measured; a name makes it a surface of its own to rate (#468). Returns its id.
 */
export async function addMeasuredSurface(areaId: string, type: SurfaceType, name = ''): Promise<string> {
  let surfaceId = '';
  await changeSurfaces(areaId, (doc) => {
    const added = addSurface(doc, type);
    surfaceId = added.surfaces[added.surfaces.length - 1].id;
    return renameSurface(added, surfaceId, name);
  });
  return surfaceId;
}

/** Name (or rename) a measured surface, as the Measure tab's name box does. */
export const renameMeasuredSurface = (areaId: string, surfaceId: string, name: string): Promise<void> =>
  changeSurfaces(areaId, (doc) => renameSurface(doc, surfaceId, name));

function bandChoice(visit: DeviceRecord, rating: Fields[string]): string | null {
  const band = bandOf(scaleKeyOf(visit.fields.hig_ratingscale), typeof rating === 'string' ? rating : null);
  return band ? BAND_CHOICE[band] : null;
}

/** Where, which way, when and how a photo was taken, for its row (photo-place.ts). */
export interface PhotoPlace {
  takenAt: string;
  bearing: number | null;
  position: { latitude: number; longitude: number; accuracy: number } | null;
  /** Taken in the app, or chosen from the library (#436). */
  method: 'Camera' | 'Library';
  /** Make and model, where the file says. */
  camera: string | null;
}

/** A photo with no row yet: uploaded before the rows (the area's older list), or taken on the older build. */
interface EarlierPhoto {
  fileName: string;
  surfaceId: string | null;
  feature: boolean;
  place: Omit<PhotoPlace, 'method' | 'camera'>;
}

/** An area's photos that have no row, from the older list and this device, with where and when they were taken. */
async function photosWithoutRows(area: DeviceRecord, haveRow: ReadonlySet<string>): Promise<Map<string, EarlierPhoto>> {
  const photos = new Map<string, EarlierPhoto>();
  for (const p of uploadedList(area.fields.hig_photos)) {
    const position = typeof p.latitude === 'number' && typeof p.longitude === 'number' ? { latitude: p.latitude, longitude: p.longitude, accuracy: p.accuracy ?? 0 } : null;
    photos.set(p.photoId, { fileName: p.name, surfaceId: p.surfaceId ?? null, feature: !!p.areaPhoto, place: { takenAt: p.uploadedOn ?? new Date().toISOString(), bearing: p.bearing ?? null, position } });
  }
  for (const p of await listPhotos(area.id)) {
    photos.set(p.id, { fileName: p.fileName, surfaceId: p.surfaceId ?? null, feature: !!p.areaPhoto, place: { takenAt: p.takenAt, bearing: p.bearing ?? null, position: p.position ?? null } });
  }
  for (const id of haveRow) photos.delete(id);
  return photos;
}

/**
 * Photos taken before the condition report started (while measuring), or on
 * another device, become its evidence too, with the position and direction
 * they were taken with, so they pin on the site plan (DD, 2026-10-21): each
 * row is linked to the visit and the area's visit area. A photo with no row
 * yet (from before the rows) gets one. Ids are the photos' own, so running this
 * again — here or on another device — adds nothing.
 */
export async function addEarlierPhotos(jobId: string): Promise<number> {
  const records = await listRecords(jobId);
  const visit = visitOf(records);
  if (!visit) return 0;
  const removed = new Set([...((await getJob(jobId))?.removedPhotoIds ?? []), ...removingIds(records)]);
  // A photo not moved yet already has its evidence row: a photo row under the same id would clash with it.
  const haveRow = new Set([...records.filter((r) => r.table === PHOTO_TABLE || r.table === UNMOVED_EVIDENCE).map((r) => r.id), ...removed]);
  let added = 0;
  for (const area of records.filter((r) => r.table === 'area')) {
    const unlinked = photoRowsOfArea(records, area.id).filter((r) => typeof r.fields.hig_visitareaid !== 'string');
    const missing = await photosWithoutRows(area, haveRow);
    if (!unlinked.length && !missing.size) continue;
    const visitArea = await ensureVisitArea(visit, area);
    const condition = { visitId: visit.id, visitAreaId: visitArea.id };
    for (const row of unlinked) {
      const purpose = row.fields.hig_purpose ?? 'Before';
      await patchFields(row.id, { hig_sitevisitid: visit.id, hig_visitareaid: visitArea.id, hig_purpose: purpose });
      added++;
    }
    const measureId = (await getRecord(area.parentId))?.parentId;
    if (!measureId) continue;
    for (const [id, photo] of missing) {
      const fields = earlierPhotoFields({ ...photo, areaId: area.id }, photo.place, condition);
      await saveRecord({ id, table: PHOTO_TABLE, jobId, parentId: measureId, etag: null, fields });
      added++;
    }
  }
  if (added) void syncNow();
  return added;
}

/** Take every pin off: the site plan was remade, so their places were on another image. */
export async function clearPins(jobId: string): Promise<void> {
  for (const e of evidenceOf(await listRecords(jobId))) {
    if (e.fields.hig_pinx !== null && e.fields.hig_pinx !== undefined) await patchFields(e.id, { hig_pinx: null, hig_piny: null, hig_pinplacedbyhand: false });
  }
}

/** Take every access line off: like the pins, they were drawn on the old plan's image. */
export async function clearAccessLines(jobId: string): Promise<void> {
  for (const v of (await listRecords(jobId)).filter((r) => r.table === 'visitarea')) {
    if (typeof v.fields.hig_accesslines === 'string') await patchFields(v.id, { hig_accesslines: null });
  }
}

/** Put a photo's pin on the site plan (0–1 across and down), or move it by hand. */
export const placePin = (evidenceId: string, x: number, y: number, byHand: boolean): Promise<void> =>
  patchFields(evidenceId, { hig_pinx: round4(x), hig_piny: round4(y), hig_pinplacedbyhand: byHand });

const round4 = (n: number) => Math.round(Math.min(1, Math.max(0, n)) * 10000) / 10000;
